import React, { useState, useEffect } from "react";
import { Modal, Form, Alert, Button as BSButton, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarcode, faTimes, faInfoCircle, faPlus, faCheck, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Button from "components/base/Button";
import { useEmpresas } from "providers/EmpresaProvider";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { balancoService, ProdutoParaBalancoType } from "services/balancoService";
import { balancoKeys } from "hooks/useBalanco";
import { convertDateToUTC } from "utils/dateUtils";

interface BotaoCodigoBarrasProps {
    disabled?: boolean;
    variant?: "primary" | "secondary" | "success" | "danger" | "warning" | "info" | "outline-primary" | "outline-secondary";
    size?: "sm" | "lg";
    className?: string;
}

const BotaoCodigoBarras: React.FC<BotaoCodigoBarrasProps> = ({
    disabled = false,
    variant = "primary",
    size,
    className = ""
}) => {
    // Estados da modal
    const [showModal, setShowModal] = useState(false);
    const [codigoBarras, setCodigoBarras] = useState('');
    const [produtosBipados, setProdutosBipados] = useState<string[]>([]);

    // Estados para produto controlado
    const [produtoControlado, setProdutoControlado] = useState<ProdutoParaBalancoType | null>(null);
    const [showModalControlado, setShowModalControlado] = useState(false);
    const [quantidade, setQuantidade] = useState(1);
    const [lote, setLote] = useState('');
    const [validade, setValidade] = useState('');

    // Estado para controlar se o modal de bipagem deve estar temporariamente oculto
    const [modalBipagemTemporariamenteOculto, setModalBipagemTemporariamenteOculto] = useState(false);

    // Hooks
    const queryClient = useQueryClient();
    const { selectedIds } = useEmpresas();
    const { getMutationErrorHandler } = usePermissionModal();
    const empresaId = selectedIds[0];

    // Estado para exibir mensagens de erro
    const [mensagemErro, setMensagemErro] = useState<string | null>(null);

    // Mutation para incluir produto por código de barras
    const incluirPorCodigoBarrasMutation = useMutation({
        mutationFn: (codigoBarras: string) => balancoService.incluirProdutoPorCodigoBarras(codigoBarras, empresaId),
        onSuccess: (data) => {
            console.log('Resposta do backend para bipagem:', data);

            // Limpar mensagem de erro anterior
            setMensagemErro(null);

            console.log('Produto encontrado:', data);
            console.log('É controlado?', data.controleEspecial);
            if (data.controleEspecial) {
                // Produto controlado - abrir modal para lote/validade
                console.log('Abrindo modal para produto controlado');
                setProdutoControlado(data);
                setModalBipagemTemporariamenteOculto(true); // Ocultar modal de bipagem temporariamente
                setShowModalControlado(true);
            } else {
                // Produto normal - já foi incluído automaticamente
                console.log('Produto normal incluído automaticamente');
                setProdutosBipados(prev => [...prev, data.nome]);
                setCodigoBarras('');
                // Focar no campo para próxima bipagem
                setTimeout(() => {
                    const input = document.getElementById('campo-bipagem');
                    if (input) input.focus();
                }, 100);
            }
            // Invalidar cache do balanço
            queryClient.invalidateQueries({ queryKey: balancoKeys.all });
        },
        onError: getMutationErrorHandler("Erro ao incluir produto por código de barras.", (data) => {
            console.log('Erro de negócio:', data.message);
            setMensagemErro(data.message || 'Erro desconhecido');
            setCodigoBarras('');
            // Focar no campo para nova tentativa
            setTimeout(() => {
                const input = document.getElementById('campo-bipagem');
                if (input) input.focus();
            }, 100);
        })
    });

    // Mutation para incluir produto controlado
    const incluirProdutoControladoMutation = useMutation({
        mutationFn: (dto: { produtoId: number; quantidade: number; lote: string; validade: string }) =>
            balancoService.incluirProduto(dto, empresaId),
        onSuccess: (data) => {
            if (data.isSuccess && produtoControlado) {
                // Adicionar à lista de bipados
                setProdutosBipados(prev => [...prev, produtoControlado.nome]);

                // Limpar estados do modal controlado
                setShowModalControlado(false);
                setModalBipagemTemporariamenteOculto(false); // Reabrir modal de bipagem
                setProdutoControlado(null);
                setQuantidade(1);
                setLote('');
                setValidade('');
                setCodigoBarras('');

                // Focar no campo para próxima bipagem
                setTimeout(() => {
                    const input = document.getElementById('campo-bipagem');
                    if (input) input.focus();
                }, 100);

                // Invalidar cache do balanço
                queryClient.invalidateQueries({ queryKey: balancoKeys.all });
            }
        },
        onError: getMutationErrorHandler("Erro ao incluir produto controlado.")
    });

    // Função para processar bipagem
    const processarBipagem = (codigo: string) => {
        if (!codigo.trim()) return;
        incluirPorCodigoBarrasMutation.mutate(codigo);
    };

    // Handler para Enter no campo de bipagem
    const handleBipagemKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            processarBipagem(codigoBarras);
        }
    };

    // Função para abrir modal de bipagem
    const abrirModal = () => {
        setShowModal(true);
        setProdutosBipados([]);
        setCodigoBarras('');
    };

    // Função para fechar modal de bipagem
    const fecharModal = () => {
        setShowModal(false);
        setCodigoBarras('');
        setProdutosBipados([]);
        setMensagemErro(null); // Limpar mensagem de erro
    };

    // Função para incluir produto controlado
    const incluirControlado = () => {
        console.log('Tentando incluir produto controlado:', {
            produtoControlado,
            lote,
            validade,
            quantidade
        });

        if (!produtoControlado || !lote.trim() || !validade || quantidade <= 0) {
            console.log('Validação falhou:', {
                temProduto: !!produtoControlado,
                temLote: !!lote.trim(),
                temValidade: !!validade,
                quantidadeValida: quantidade > 0
            });
            return;
        }

        console.log('Enviando mutation para incluir produto controlado');

        incluirProdutoControladoMutation.mutate({
            produtoId: produtoControlado.id,
            quantidade,
            lote,
            validade: convertDateToUTC(validade) || validade
        });
    };

    // Focar no campo quando modal abre
    useEffect(() => {
        if (showModal) {
            const timer = setTimeout(() => {
                const input = document.getElementById('campo-bipagem');
                if (input) input.focus();
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [showModal]);

    return (
        <>
            <Button
                variant={variant}
                size={size}
                className={className}
                onClick={abrirModal}
                disabled={disabled}
            >
                <FontAwesomeIcon icon={faBarcode} className="me-2" />
                Código de Barras
            </Button>

            {/* Modal de Bipagem */}
            <Modal show={showModal && !modalBipagemTemporariamenteOculto} onHide={fecharModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faBarcode} className="me-2" />
                        Bipagem de Produtos
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle mb-4">
                        <div className="text-info-emphasis">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            <strong>Modo Bipagem Ativa:</strong> Bipe os códigos de barras dos produtos para incluí-los automaticamente no balanço.
                            Para produtos controlados, será solicitado lote e validade.
                        </div>
                    </Alert>

                    <Form.Group className="mb-4">
                        <Form.Label htmlFor="campo-bipagem" className="fw-bold">
                            <FontAwesomeIcon icon={faBarcode} className="me-2" />
                            Campo de Bipagem
                        </Form.Label>
                        <Form.Control
                            id="campo-bipagem"
                            type="text"
                            size="lg"
                            placeholder="Posicione o cursor aqui e bipe o código de barras..."
                            value={codigoBarras}
                            onChange={(e) => {
                                setCodigoBarras(e.target.value);
                                // Limpar mensagem de erro quando usuário começar a digitar
                                if (mensagemErro) setMensagemErro(null);
                            }}
                            onKeyPress={handleBipagemKeyPress}
                            disabled={incluirPorCodigoBarrasMutation.isPending}
                            autoFocus
                        />
                        <Form.Text className="text-body-secondary">
                            Pressione Enter após bipar ou digitar o código de barras
                        </Form.Text>
                    </Form.Group>

                    {/* Mensagem de erro de negócio */}
                    {mensagemErro && (
                        <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle mb-4">
                            <div className="text-warning-emphasis">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                <strong>Atenção:</strong> {mensagemErro}
                            </div>
                        </Alert>
                    )}

                    {/* Lista de produtos bipados na sessão atual */}
                    {produtosBipados.length > 0 && (
                        <div>
                            <h6 className="mb-3">
                                <FontAwesomeIcon icon={faCheck} className="me-2 text-success-emphasis" />
                                Produtos Incluídos Nesta Sessão ({produtosBipados.length})
                            </h6>
                            <div className="bg-body-secondary p-3 rounded mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {produtosBipados.map((produto, index) => (
                                    <div key={index} className="d-flex align-items-center mb-2">
                                        <FontAwesomeIcon icon={faCheck} className="text-success-emphasis me-2" size="sm" />
                                        <span className="small">{produto}</span>
                                        <Badge bg="success" className="ms-auto">Incluído</Badge>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {incluirPorCodigoBarrasMutation.isError && (
                        <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle">
                            <div className="text-danger-emphasis">
                                <strong>Erro:</strong> {(incluirPorCodigoBarrasMutation.error as Error).message}
                            </div>
                        </Alert>
                    )}

                    {incluirPorCodigoBarrasMutation.isPending && (
                        <div className="text-center py-2">
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Processando...</span>
                            </div>
                            Processando código de barras...
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <div className="text-body-secondary small">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                        Mantenha este modal aberto para bipagem contínua
                    </div>
                    <BSButton variant="secondary" onClick={fecharModal}>
                        <FontAwesomeIcon icon={faTimes} className="me-2" />
                        Fechar
                    </BSButton>
                </Modal.Footer>
            </Modal>

            {/* Modal para produtos controlados */}
            {produtoControlado && (
                <Modal
                    show={showModalControlado}
                    onHide={() => {
                        setShowModalControlado(false);
                        setModalBipagemTemporariamenteOculto(false); // Reabrir modal de bipagem
                        setProdutoControlado(null);
                        setQuantidade(1);
                        setLote('');
                        setValidade('');
                    }}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            Produto Controlado
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mb-3">
                            <strong>{produtoControlado.nome}</strong>
                            <div className="small text-body-secondary">
                                Código: {produtoControlado.codigo} | Estoque Atual: {produtoControlado.estoqueAtual}
                            </div>
                        </div>

                        <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle mb-3">
                            <div className="text-warning-emphasis">
                                <FontAwesomeIcon icon={faBarcode} className="me-2" />
                                <strong>Produto Controlado:</strong> É obrigatório informar lote e validade.
                            </div>
                        </Alert>

                        <Form.Group className="mb-3">
                            <Form.Label>Quantidade Contada *</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                value={quantidade}
                                onChange={(e) => setQuantidade(parseInt(e.target.value) || 0)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Lote *</Form.Label>
                            <Form.Control
                                type="text"
                                value={lote}
                                onChange={(e) => setLote(e.target.value.toUpperCase())}
                                placeholder="Digite o lote"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Validade *</Form.Label>
                            <Form.Control
                                type="date"
                                value={validade}
                                onChange={(e) => setValidade(e.target.value)}
                            />
                        </Form.Group>

                        {incluirProdutoControladoMutation.isError && (
                            <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle">
                                <div className="text-danger-emphasis">
                                    <strong>Erro:</strong> {(incluirProdutoControladoMutation.error as Error).message}
                                </div>
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <BSButton
                            variant="secondary"
                            onClick={() => {
                                setShowModalControlado(false);
                                setModalBipagemTemporariamenteOculto(false); // Reabrir modal de bipagem
                                setProdutoControlado(null);
                                setQuantidade(1);
                                setLote('');
                                setValidade('');
                            }}
                            disabled={incluirProdutoControladoMutation.isPending}
                        >
                            <FontAwesomeIcon icon={faTimes} className="me-2" />
                            Cancelar
                        </BSButton>
                        <BSButton
                            variant="primary"
                            onClick={incluirControlado}
                            disabled={!lote.trim() || !validade || quantidade <= 0 || incluirProdutoControladoMutation.isPending}
                        >
                            {incluirProdutoControladoMutation.isPending ? (
                                <>
                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                        <span className="visually-hidden">Carregando...</span>
                                    </div>
                                    Incluindo...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    Incluir no Balanço
                                </>
                            )}
                        </BSButton>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
};

export default BotaoCodigoBarras; 